import { type FC } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import {
  EditableRole,
  MemberInformationRoleFormSchema,
} from 'features/Team/components/MemberInformationRoleForm/schema';
import { MemberRoleLayout } from 'features/Team/components/MemberRoleLayout';
import { isFreeRole } from 'features/Team/utils';
import logger from 'helpers/logger';

import * as routes from '../../../../../../routes';
import { useGiveLegalBeneficiaryAccess } from '../../../../hooks/useGiveLegalBeneficiaryAccess';
import { useInviteTeamMember } from '../../../../hooks/useInviteTeamMember';
import { useMemberInviteState } from '../../../MemberInviteProvider';
import { getLocalizedError } from '../../utils';
import { useInvitationTransitions } from './hooks/useInvitationTransitions';
import locales from './locales';

const MemberRole: FC<StepProps> = ({ goBack, goNext }) => {
  const history = useHistory();

  const {
    dispatch,
    isFreeRolesOnly,
    memberInformation,
    memberRole,
    selectedLegalBeneficiary,
  } = useMemberInviteState();
  const [
    giveLegalBeneficiaryAccess,
    { loading: giveLegalBeneficiaryAccessLoading },
  ] = useGiveLegalBeneficiaryAccess();
  const { showToast } = useToast();
  const [
    submitInviteMemberRequest,
    { loading: submitInviteMemberRequestLoading },
  ] = useInviteTeamMember();

  const transitions = useInvitationTransitions();

  const cancelFlow = () => {
    history.push(routes.members);
  };

  const handleRoleChange = (role: EditableRole) => {
    dispatch({
      payload: {
        role,
      },
      type: 'SUBMIT_INVITE_ROLE',
    });
  };

  const handleSubmit: SubmitHandler<MemberInformationRoleFormSchema> = async (
    data,
  ) => {
    try {
      dispatch({
        payload: data,
        type: 'SUBMIT_INVITE_ROLE',
      });

      // only do request here for accountants as we are not showing
      // next step in this case
      if (isFreeRole(data.role)) {
        if (
          selectedLegalBeneficiary &&
          selectedLegalBeneficiary.companyUserId
        ) {
          await giveLegalBeneficiaryAccess({
            companyUserId: selectedLegalBeneficiary.companyUserId,
            role: data.role,
          });
        } else {
          // Use data directly since otherwise we will need to wait 1 render
          // for the reducer to get updated.
          // @see https://linear.app/shine/issue/OPS-14075/probleme-ajout-comptable
          await submitInviteMemberRequest({
            ...memberInformation,
            ...data,
          });
        }

        history.push(routes.members);
        showToast({
          message: locales.formatString(
            selectedLegalBeneficiary
              ? locales.roleAppliedConfirmation
              : locales.invitationConfirmation,
            {
              firstName: memberInformation.inviteeFirstName || '',
            },
          ),
          type: 'success',
        });
      } else {
        goNext();
      }
    } catch (error) {
      const localizedError = getLocalizedError(error);
      showToast({
        message: localizedError,
        type: 'error',
      });
      logger.error('Could not invite additional team member', {
        context: { error },
      });
    }

    goNext();
  };

  return (
    <MemberRoleLayout
      applyIcon={
        memberRole.role && isFreeRole(memberRole.role) ? 'send' : undefined
      }
      applyLabel={
        memberRole.role && isFreeRole(memberRole.role)
          ? locales.sendInvitation
          : locales.submit
      }
      isFreeRolesOnly={isFreeRolesOnly}
      isLoading={
        giveLegalBeneficiaryAccessLoading || submitInviteMemberRequestLoading
      }
      onApplyClicked={handleSubmit}
      onBackClicked={goBack}
      onCancelClicked={cancelFlow}
      onRoleChanged={handleRoleChange}
      roleTransitions={transitions}
      selectedRole={memberRole.role}
      showFreeRolesTag={true}
      title={isFreeRolesOnly ? locales.titleAccountantOnly : locales.title}
    />
  );
};

export default MemberRole;
